import {
  Component,
  OnInit,
  NgZone,
  ViewChildren,
  QueryList,
  AfterViewInit,
  OnDestroy,
} from "@angular/core";

import {
  DetailedOrderStatusRequestPromiseService,
  TradeDealPromiseService,
  SystemConfigPromiseService,
  SysFeatures,
  CustomFieldPromiseLookupService,
} from "shared/cubiq-services";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { GoldenLayoutPopout } from "../golden-layout/golden-layout-popout";
import { TranslateService } from "@ngx-translate/core";
import { ElementRef } from "@angular/core";
import { DatePipe } from "@angular/common";
import { TradeDeal } from "shared/cubiq-services";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

// Migration must be kept in sync with Table Definition model
export const EMBEDDED_COMPONENT_CONFIG_VER = "2017112101";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { FixingSourceAdapter } from "shared/cubiq-services/adapter/common/FixingSourceAdapter";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { BfxConfigIDRefHelper } from "../../shared/helper-services/bfx-config-id-ref-helper";
import { DataTable } from "@bfx/primeng/datatable";
import { NativeDesktopTable } from "../bfx-native-desktop/native-desktop-table";
import { first, Subscription } from "rxjs";
import { BfxClipboardService } from "shared/helper-services/bfx-clipboard.service";

@Component({
  selector: `bfx-deal-details`,
  templateUrl: "./html/bfx-deal-details.html",
  standalone: false,
})
export class BfxDealDetailsComponent
  implements AfterViewInit, OnInit, OnDestroy, GoldenLayoutComponent
{
  public dealId: string;
  public parentPopup: GoldenLayoutPopout;
  public scrollHeight = 140;
  public displayFillUser: boolean = false;
  public fwdEnabled: boolean = false;
  public ndfEnabled: boolean = false;
  public isBmark: boolean = false;
  public fixingSrcAdapter: FixingSourceAdapter;
  public isDesktop: boolean = false;
  public oboEnabled: boolean = false;
  public intentType: CubiqTS.OrderIntent = null;

  // make available to template
  public BfxConfigIDRefHelper = BfxConfigIDRefHelper;

  @ViewChildren("tradeRates")
  public tradeRatesTable: QueryList<DataTable>;

  public summary: string = null;

  public OI = CubiqTS.OrderIntent;

  /** List of visible custom fields sorted by ID */
  customFieldDefns: Array<CubiqTS.CustomField>;

  tradeRatesTableSubscription: Subscription;

  constructor(
    private _detailedOrderService: DetailedOrderStatusRequestPromiseService,
    private _clipboardService: BfxClipboardService,
    private _tradeDealService: TradeDealPromiseService,
    private _translationService: TranslateService,
    private ngZone: NgZone,
    private elementRef: ElementRef,
    private datePipe: DatePipe,
    private sysConfigService: SystemConfigPromiseService,
    private configService: BfxConfigService,
    protected customFieldService: CustomFieldPromiseLookupService,
  ) {
    this.isDesktop = NativeDesktopSupport.isNativeDesktopContainer();
    configService.getUIConfig().then((config) => {
      this.oboEnabled = config && config.general ? config.general.oboEnabled : false;
    });
  }

  ngOnDestroy(): void {
    if (this.tradeRatesTableSubscription != null) {
      this.tradeRatesTableSubscription.unsubscribe();
    }
  }

  public ngAfterViewInit(): void {
    // FXBFXOM-368 directive binding doesn't seem to work
    this.tradeRatesTable.forEach((dt) => NativeDesktopTable.initialize(dt));

    this.tradeRatesTableSubscription = this.tradeRatesTable.changes.subscribe((ql) => {
      ql.forEach((dt) => NativeDesktopTable.initialize(dt));
    });
  }

  public dealDetails: TradeDeal = new TradeDeal();

  public control: any = {
    mode: true,
    hasDealDetails: false,
    fwdEnabled: false,
    ndfEnabled: false,
  };

  public config: any = {
    debug: false,
  };

  ngOnInit(): void {
    this.sysConfigService.getSystemConfig(SysFeatures.displayUserNames).then((value) => {
      this.displayFillUser = "Y" == value;
    });
    this.sysConfigService.getSystemConfig(SysFeatures.fwdEnabled).then((value) => {
      this.fwdEnabled = "Y" == value;
    });
    this.sysConfigService.getSystemConfig(SysFeatures.ndfEnabled).then((value) => {
      this.ndfEnabled = "Y" == value;
    });

    this.fixingSrcAdapter = new FixingSourceAdapter(this._translationService);
  }

  // Golden layout popout component creation callback
  glInit(data: any, container: any, state: any): void {
    this.summary = null;

    if (data.tradeDealId) {
      this.dealId = data.tradeDealId;
      this._tradeDealService
        .getTradeDealDetails(this.dealId, true, true)
        .then((tradeDeal: TradeDeal) => {
          if (
            tradeDeal instanceof Object &&
            tradeDeal.hasOwnProperty("dealId") &&
            tradeDeal.dealId === this.dealId
          ) {
            this.dealDetails = Object.assign(new TradeDeal(), tradeDeal);
            // NDF
            if (this.dealDetails.productType == "NDF") {
              this.control.ndfEnabled = this.ndfEnabled;
              // need to make it a taste bigger as we will have an extra row of fields.
              this.parentPopup.addContainerClass("ndf");
            } else {
              this.control.ndfEnabled = false;
            }
            // Benchmark
            if (parseInt(this.dealDetails.orderTypeId) === CubiqTS.OrderType.BENCHMARK) {
              this.control.isBmark = true;
              this.parentPopup.addContainerClass("ndf");
            }

            let vd = this.formatUTCDate(this.dealDetails.valueDate);

            this.dealDetails.fixingCode = this.fixingSrcAdapter.retrieveData(
              this.dealDetails,
              "fixingCode",
            );

            let ref: string = BfxConfigIDRefHelper.getDealRef(this.dealDetails);
            let winTitle: string = this._translationService.instant("trade.details.title", [ref]);

            this.summary =
              this.dealDetails.clientId +
              " " +
              this.dealDetails.instrument +
              " " +
              this.dealDetails.dealDirStr +
              " " +
              (this.dealDetails.orderTypeName == "CALL"
                ? ""
                : this.dealDetails.dealtAmtStr + " " + this.dealDetails.dealtCcy) +
              " " +
              vd;

            this.intentType = tradeDeal.dealDir
              ? CubiqTS.OrderIntent.BUY
              : CubiqTS.OrderIntent.SELL;

            this.parentPopup.title = winTitle;
            this.control.hasDealDetails = true;
          } else {
            logger.out.error(`ERROR: Invalid TradeDeal retrieved for ${this.dealId}`, tradeDeal);
          }

          this.initializeCustomFieldDefns();
        });
    }
  }

  public formatUTCDate(dateTime: number): string {
    if (dateTime) {
      const date = CubiqTS.moment(dateTime);
      return date.utc().format("DD-MMM-YY");
    }
    return "-";
  }

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopup = parentPopup;
    this.parentPopup.setShowDock(false);
    this.parentPopup.setShowRefresh(false);
  }

  close($event: MouseEvent) {
    this.parentPopup.close();
  }

  onClose(): boolean {
    return true;
  }

  /**
   * Retrieve all custom field definitions, strip out the non-enditable ones and sort by field ID
   */
  private initializeCustomFieldDefns(): void {
    this.customFieldService.getActiveCustomFields().then((customFields) => {
      const visibleFieldDefns: Array<CubiqTS.CustomField> = [];
      for (let defn of customFields) {
        if (defn.isEnabledFg) {
          // We only allow a maximum of 4 editable fields
          if (defn.showOnNewModifyOrderUiFg && visibleFieldDefns.length < 4) {
            visibleFieldDefns.push(defn);
          }
        }
      }
      this.customFieldDefns = visibleFieldDefns;
    });
  }

  public customFieldsEnabled(): boolean {
    return this.customFieldDefns && this.customFieldDefns.length > 0;
  }

  public customFieldEnabled(index: number): boolean {
    return this.customFieldDefns[index] && this.customFieldDefns[index].isEnabledFg;
  }

  public customFieldHeading(index: number): string {
    if (this.customFieldEnabled(index)) {
      return this.customFieldDefns[index].displayName;
    }
    return null;
  }

  public customFieldValue(index: number): string {
    if (this.customFieldEnabled(index) && this.dealDetails.customValues) {
      let fieldId = this.customFieldDefns[index].fieldId;
      let fieldValue: string = null;
      for (let custValue of this.dealDetails.customValues) {
        if (custValue.fieldId === fieldId) {
          fieldValue = custValue.value;
          break;
        }
      }
      return fieldValue;
    }
    return "";
  }
}
